<script lang="ts">
  // Attachments footer (spec §10): named non-inline parts as chips — previewable ones open the
  // viewer with `&inline=1`, the rest download — plus a zip link when there is more than one.
  import { api } from '$lib/api/client';
  import type { ApiAttachment } from '$lib/api/types';
  import { t } from '$lib/i18n';
  import { formatSize } from '$lib/mail/format';
  import Icon from '../ui/Icon.svelte';
  import { isPreviewable, namedAttachments, type ViewerSpec } from './_local';

  let {
    folder,
    uid,
    attachments,
    onpreview,
  }: {
    folder: string;
    uid: number;
    attachments: readonly ApiAttachment[];
    onpreview: (v: ViewerSpec) => void;
  } = $props();

  const list = $derived(namedAttachments(attachments));

  function open(a: ApiAttachment): void {
    onpreview({
      url: api.attachmentUrl(folder, uid, a.part, true),
      dlUrl: api.attachmentUrl(folder, uid, a.part),
      type: a.type,
      name: a.filename ?? '',
    });
  }
</script>

{#if list.length}
  <footer class="atts">
    <div class="atts-head">
      <h2>{t('attachments')}</h2>
      {#if list.length > 1}
        <a class="allbtn" download href={api.attachmentsZipUrl(folder, uid)}><Icon name="down" size={14} /> {t('saveAllAtts')} ({list.length})</a>
      {/if}
    </div>
    <div class="chips">
      {#each list as a (a.part)}
        {#if isPreviewable(a.type)}
          <button type="button" class="chip" aria-label="{t('preview')}: {a.filename}" onclick={() => open(a)}>
            <Icon name={a.type.toLowerCase().startsWith('image/') ? 'image' : 'draft'} size={14} /><span dir="ltr">{a.filename}</span><small>{formatSize(a.size)}</small>
          </button>
        {:else}
          <a class="chip" download={a.filename} href={api.attachmentUrl(folder, uid, a.part)} aria-label="{t('download')}: {a.filename}">
            <Icon name="down" size={14} /><span dir="ltr">{a.filename}</span><small>{formatSize(a.size)}</small>
          </a>
        {/if}
      {/each}
    </div>
  </footer>
{/if}

<style>
  /* svelte-rwh28b (Reader) — attachments footer */
  .atts {
    margin-top: var(--space-8);
    border-top: 1px solid var(--border-subtle);
    padding-top: var(--space-4)
  }

  .atts h2 {
    font-size: var(--text-sm);
    color: var(--text-muted);
    margin: 0
  }

  .atts-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2);
    margin-bottom: var(--space-3)
  }

  .allbtn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    padding: var(--space-1) var(--space-3);
    min-block-size: var(--control-sm);
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--text-body);
    background: var(--surface-raised);
    text-decoration: none
  }

  .allbtn:hover {
    background: var(--surface-hover)
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2)
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    padding: var(--space-1) var(--space-3);
    min-block-size: var(--control-sm);
    font-size: var(--text-xs);
    color: var(--text-body);
    max-width: 100%
  }

  .chip:hover {
    background: var(--surface-subtle)
  }

  .chip small {
    color: var(--text-faint)
  }
</style>
